// Mini Switcher — practice-only UI for the Equipment Lab.
//
// This is NOT part of any real hardware panel. It sits outside the PTZ
// controller (or any other panel) so trainees can simulate the PGM/PVW
// workflow that the Technical Director handles on the real switcher.
//
// Doctrine: the PTZ operator does NOT control what is on air. The TD
// does. This switcher exists only to help trainees understand the
// relationship between their framing work and what the audience sees.

import type { MultiviewBusSource } from "./multiview-layout";

const SOURCE_BUTTONS: {
  id: MultiviewBusSource;
  label: string;
  color: string;
}[] = [
  { id: "cam1", label: "CAM 1", color: "#3a7bd5" },
  { id: "cam2", label: "CAM 2", color: "#3a7bd5" },
  { id: "cam3", label: "CAM 3", color: "#3a7bd5" },
  { id: "media1", label: "MEDIA 1", color: "#c49a3c" },
  { id: "media2", label: "MEDIA 2", color: "#c49a3c" },
  { id: "black", label: "BLACK", color: "#5a5d66" },
];

export interface MiniSwitcherProps {
  pgmSource: MultiviewBusSource;
  pvwSource: MultiviewBusSource;
  onPvwChange: (source: MultiviewBusSource) => void;
  onTake: () => void;
}

export function MiniSwitcher({
  pgmSource,
  pvwSource,
  onPvwChange,
  onTake,
}: MiniSwitcherProps) {
  return (
    <div className="mini-switcher" role="group" aria-label="Practice switcher">
      <div className="mini-switcher__note">
        <span className="mini-switcher__note-dot" aria-hidden />
        Practice switcher — not part of the PTZ controller
      </div>

      <div className="mini-switcher__body">
        {/* Bus displays */}
        <div className="mini-switcher__buses">
          <div className="mini-switcher__bus mini-switcher__bus--pgm">
            <span className="mini-switcher__bus-label">PGM</span>
            <span className="mini-switcher__bus-source">
              {SOURCE_BUTTONS.find((s) => s.id === pgmSource)?.label ??
                pgmSource.toUpperCase()}
            </span>
          </div>
          <div className="mini-switcher__bus mini-switcher__bus--pvw">
            <span className="mini-switcher__bus-label">PVW</span>
            <span className="mini-switcher__bus-source">
              {SOURCE_BUTTONS.find((s) => s.id === pvwSource)?.label ??
                pvwSource.toUpperCase()}
            </span>
          </div>
        </div>

        {/* Source row */}
        <div className="mini-switcher__sources">
          {SOURCE_BUTTONS.map((s) => {
            const isPvw = pvwSource === s.id;
            return (
              <button
                key={s.id}
                type="button"
                className={`mini-switcher__src-btn ${isPvw ? "mini-switcher__src-btn--pvw" : ""}`}
                onClick={() => onPvwChange(s.id)}
                aria-label={`Arm ${s.label} on preview`}
                aria-pressed={isPvw}
              >
                {s.label}
              </button>
            );
          })}
        </div>

        {/* Take */}
        <button
          type="button"
          className="mini-switcher__take"
          onClick={onTake}
          aria-label="Take preview to program"
        >
          TAKE
        </button>
      </div>

      <style>{MINI_SWITCHER_STYLES}</style>
    </div>
  );
}

const MINI_SWITCHER_STYLES = `
.mini-switcher {
  font-family: -apple-system, "SF Pro Text", system-ui, sans-serif;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
}
.mini-switcher__note {
  font-size: 11px;
  color: #8a7e6b;
  letter-spacing: 0.3px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.mini-switcher__note-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #3a7bd5;
  display: inline-block;
}
.mini-switcher__body {
  display: flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(180deg, #f8f6f2 0%, #f0ece4 100%);
  border: 1px solid #d9d2c3;
  border-radius: 8px;
  padding: 10px 14px;
  box-shadow: 0 2px 8px rgba(27, 22, 10, 0.06);
}

/* Bus displays */
.mini-switcher__buses {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.mini-switcher__bus {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 5px;
  min-width: 90px;
}
.mini-switcher__bus--pgm {
  background: linear-gradient(180deg, #fdf3f3 0%, #f5e0e0 100%);
  border: 1.5px solid #e08e8e;
}
.mini-switcher__bus--pvw {
  background: linear-gradient(180deg, #f3faf3 0%, #e0f0e0 100%);
  border: 1.5px solid #8ec08e;
}
.mini-switcher__bus-label {
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 1px;
  color: #6e6452;
  width: 28px;
}
.mini-switcher__bus-source {
  font-size: 12px;
  font-weight: 700;
  color: #2b2c2f;
  font-variant-numeric: tabular-nums;
}

/* Source buttons */
.mini-switcher__sources {
  display: flex;
  gap: 5px;
}
.mini-switcher__src-btn {
  padding: 7px 10px;
  border-radius: 5px;
  border: 1px solid #c9c3b5;
  background: linear-gradient(180deg, #ffffff 0%, #f5f2ec 100%);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #4a4c50;
  cursor: pointer;
  transition: all 0.12s;
  min-width: 44px;
}
.mini-switcher__src-btn:hover {
  background: #ffffff;
  border-color: #a69b88;
}
.mini-switcher__src-btn--pvw {
  background: linear-gradient(180deg, #4aa84b 0%, #3a8a3b 100%);
  color: #ffffff;
  border-color: #3a8a3b;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.2);
}
.mini-switcher__src-btn--pvw:hover {
  background: linear-gradient(180deg, #5aba5b 0%, #4aa84b 100%);
}

/* Take button */
.mini-switcher__take {
  padding: 10px 16px;
  border-radius: 5px;
  border: 1px solid #c44a4a;
  background: linear-gradient(180deg, #e85d5d 0%, #c44a4a 100%);
  color: #ffffff;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1.5px;
  cursor: pointer;
  transition: all 0.12s;
  min-width: 64px;
}
.mini-switcher__take:hover {
  background: linear-gradient(180deg, #f07070 0%, #d85a5a 100%);
}
.mini-switcher__take:active {
  transform: translateY(1px);
}
`;
